import React, { JSX, ReactElement, ReactNode, createContext, useContext } from 'react'
import { ChatMessage } from 'lib/twitchChatParse'
import { SourceMarkTarget, chatMarkTarget } from 'lib/sourceMarkUrls'
import { PLATFORM_MARKS, PLATFORM_MARK_STYLE } from 'components/chatOverlays/platformMarks'
import { OverlayPreviewSeam, previewSeamActive } from 'components/overlayPreview/previewSeam'

const PaneContext = createContext(false)

export const SourceMarkLinkPane = ({ children }: { children: ReactNode }): JSX.Element => (
  <PaneContext.Provider value>{children}</PaneContext.Provider>
)

const PageSeamContext = createContext<OverlayPreviewSeam | null>(null)

export const SourceMarkSeamProvider = ({ seam, children }: { seam: OverlayPreviewSeam | null, children: ReactNode }): JSX.Element => (
  <PageSeamContext.Provider value={seam}>{children}</PageSeamContext.Provider>
)

export const sourceMarkLinksOn = (inPane: boolean, seam: unknown): boolean => {
  if (!inPane) return false
  if (!previewSeamActive(seam)) return false
  if (seam.mode !== 'live') return false
  if ((globalThis as unknown as { obsstudio?: unknown }).obsstudio != null) return false
  return true
}

export const useSourceMarkLinks = (seam: unknown): boolean =>
  sourceMarkLinksOn(useContext(PaneContext), seam)

export const usePageSourceMarkLinks = (): boolean =>
  useSourceMarkLinks(useContext(PageSeamContext))

export const SOURCE_MARK_LINK_STYLE: React.CSSProperties = Object.freeze({
  display: 'inline-flex',
  verticalAlign: 'middle',
  flexShrink: 0,
  pointerEvents: 'auto',
  cursor: 'pointer'
})

export const openSourceMark = (url: string): void => {
  globalThis.open(url, '_blank', 'noopener,noreferrer')
}

export const sourceMarkClick = (
  e: { preventDefault: () => void, stopPropagation: () => void },
  url: string
): void => {
  e.preventDefault()
  e.stopPropagation()
  openSourceMark(url)
}

export const sourceMarkKeyDown = (
  e: { key: string, preventDefault: () => void, stopPropagation: () => void },
  url: string
): void => {
  if (e.key !== 'Enter') return
  sourceMarkClick(e, url)
}

export const SourceMarkLink = ({ target, children }: { target: SourceMarkTarget | null, children: ReactElement }): JSX.Element => {
  if (target == null) return <>{children}</>
  return (
    <span
      role='link'
      tabIndex={0}
      aria-label={target.label}
      data-testid='source-mark-link'
      style={SOURCE_MARK_LINK_STYLE}
      onClick={(e) => sourceMarkClick(e, target.url)}
      onKeyDown={(e) => sourceMarkKeyDown(e, target.url)}
    >
      {children}
    </span>
  )
}

export const ChatPlatformMark = ({ message, linked }: { message: ChatMessage, linked: boolean }): JSX.Element => (
  <SourceMarkLink target={linked ? chatMarkTarget(message) : null}>
    <img
      src={PLATFORM_MARKS[message.platform].src}
      alt={PLATFORM_MARKS[message.platform].label}
      title={PLATFORM_MARKS[message.platform].label}
      style={PLATFORM_MARK_STYLE}
    />
  </SourceMarkLink>
)
